import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { useAuth, useTheme, api } from "../App";
import AppShell from "../components/AppShell";
import {
  Copy,
  Check,
  RotateCcw,
  Calendar,
  RefreshCw,
} from "lucide-react";

const TOPIC_LABELS = {
  "ai-ml": "AI & ML",
  "cloud": "Cloud",
  "devops": "DevOps",
  "security": "Security",
  "web-dev": "Web Dev",
  "mobile": "Mobile",
  "data-science": "Data Science",
  "blockchain": "Blockchain",
  "engineering": "Engineering",
  "product": "Product",
  "ux-design": "UX Design",
  "startup": "Startup",
  "leadership": "Leadership",
  "career": "Career",
  "open-source": "Open Source",
  "programming": "Programming",
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const HistoryCard = ({ post, user, onCopy, onRestore, copiedId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const content = post.variant_content || "";
  const shouldTruncate = content.length > 300;
  const displayContent = shouldTruncate && !isExpanded
    ? content.substring(0, 300)
    : content;

  return (
    <div
      className="bg-card rounded-lg border border-border shadow-sm overflow-hidden"
      data-testid={`history-card-${post.id}`}
    >
      {/* LinkedIn-style header */}
      <div className="flex items-start gap-3 px-4 pt-3 pb-2">
        {user?.picture ? (
          <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full shrink-0" />
        ) : (
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-lg shrink-0">
            {user?.name?.charAt(0) || "U"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground leading-tight">{user?.name || "You"}</p>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5 truncate">
            {TOPIC_LABELS[post.topic] || post.topic}
          </p>
          {post.used_at && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {formatDate(post.used_at)}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-2">
        <p className="text-foreground whitespace-pre-wrap leading-relaxed text-sm">
          {displayContent}
          {shouldTruncate && !isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-muted-foreground hover:text-primary hover:underline ml-0.5"
            >
              ...see more
            </button>
          )}
        </p>
        {shouldTruncate && isExpanded && (
          <button
            onClick={() => setIsExpanded(false)}
            className="text-muted-foreground text-sm hover:text-primary hover:underline mt-1"
          >
            Show less
          </button>
        )}
      </div>

      {/* LinkedIn-style action bar */}
      <div className="border-t border-border mx-4" />
      <div className="flex items-center px-2 py-1">
        <button
          onClick={() => onCopy(post)}
          data-testid={`copy-btn-${post.id}`}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-colors ${
            copiedId === post.id
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          {copiedId === post.id ? (
            <Check className="w-5 h-5" />
          ) : (
            <Copy className="w-5 h-5" />
          )}
          <span>{copiedId === post.id ? "Copied!" : "Copy"}</span>
        </button>

        <button
          onClick={() => onRestore(post)}
          data-testid={`restore-btn-${post.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Restore to feed"
        >
          <RotateCcw className="w-5 h-5" />
          <span>Restore</span>
        </button>
      </div>
    </div>
  );
};

const HistorySkeleton = () => (
  <div className="bg-card rounded-lg border border-border overflow-hidden">
    <div className="flex items-start gap-3 px-4 pt-3 pb-2">
      <Skeleton className="w-12 h-12 rounded-full shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
    <div className="px-4 py-2 space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
    </div>
    <div className="border-t border-border mx-4" />
    <div className="flex gap-2 px-4 py-3">
      <Skeleton className="h-8 flex-1 rounded-lg" />
      <Skeleton className="h-8 flex-1 rounded-lg" />
    </div>
  </div>
);

const HistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchHistory = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const response = await api.get(`/posts/history?page=${pageNum}&limit=10`);
      const newPosts = response.data.posts;

      if (append) {
        setPosts((prev) => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setHasMore(response.data.has_more);
      setTotal(response.data.total);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch history:", error);
      toast.error("Failed to load history");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleCopy = async (post) => {
    try {
      await navigator.clipboard.writeText(post.variant_content);
      setCopiedId(post.id);
      toast.success("Post copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy post");
    }
  };

  const handleRestore = async (post) => {
    try {
      await api.post(`/posts/${post.id}/unuse`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setTotal((prev) => prev - 1);
      toast.success("Post restored to feed");
    } catch (error) {
      console.error("Failed to restore:", error);
      toast.error("Failed to restore post");
    }
  };

  const handleLoadMore = () => {
    fetchHistory(page + 1, true);
  };

  return (
    <AppShell activeNav="history">
      <div className="max-w-[555px] mx-auto px-4 py-8">
        {/* Stats */}
        <div className="mb-8 p-4 bg-muted/50 rounded-xl">
          <p className="text-sm text-muted-foreground">
            You've used <span className="font-semibold text-foreground">{total}</span> posts so far.
            Great job staying consistent!
          </p>
        </div>

        {/* History List */}
        {isLoading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <HistorySkeleton key={i} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No history yet</h3>
            <p className="text-muted-foreground mb-4">
              Posts you copy or mark as used will appear here.
            </p>
            <Button
              onClick={() => navigate("/feed")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Browse Posts
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {posts.map((post) => (
                <HistoryCard
                  key={post.id}
                  post={post}
                  user={user}
                  onCopy={handleCopy}
                  onRestore={handleRestore}
                  copiedId={copiedId}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8"
                >
                  {isLoadingMore ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
};

export default HistoryPage;
